import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowRight, ArrowUp, Columns3, GripVertical, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const read = (k: string) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };

const SKIP = /(^id$|_id$|_by$|_by_name$|^by$|^by_name$|_at$|_path$|_to$|^status$|^legacy$|^manual$|^custom$)/;
const flat = (v: any): string => (v == null ? "" : Array.isArray(v) ? v.map(flat).join(" ") : typeof v === "object" ? Object.entries(v).filter(([k]) => !SKIP.test(k)).map(([, x]) => flat(x)).join(" ") : String(v));
const dobs = (d?: string) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || ""); return m ? ` ${m[2]}/${m[3]}/${m[1]} ${+m[2]}/${+m[3]}/${m[1]}` : ""; };
const nameOf = (r: any) => ` ${r.first_name || ""} ${r.middle_name || ""} ${r.last_name || ""} ${r.name || ""} `.toLowerCase().replace(/[,.]/g, " ");

export function useSearch(rows: any[], notes?: Record<string, string>) {
  const [q, setQ] = useState("");
  const hay = useMemo(() => rows.map((r) => `${flat(r)}${dobs(r.dob)} ${notes?.[r.id] || ""}`.toLowerCase()), [rows, notes]);
  const names = useMemo(() => rows.map(nameOf), [rows]);
  const list = useMemo(() => {
    const words = q.toLowerCase().replace(/,/g, " ").split(/\s+/).filter(Boolean);
    if (!words.length) return rows;
    const byName = rows.filter((_, i) => words.every((w) => names[i].includes(` ${w}`)));
    return byName.length ? byName : rows.filter((_, i) => words.every((w) => hay[i].includes(w)));
  }, [rows, hay, names, q]);
  return { q, setQ, list };
}

const PAGE = 100;

export function usePager<T>(list: T[], resetKey: string) {
  const [n, setN] = useState(PAGE);
  useEffect(() => { setN(PAGE); }, [resetKey]);
  return { rows: list.length > n ? list.slice(0, n) : list, n, setN, total: list.length };
}

export function MoreRows({ p, testId }: { p: { n: number; setN: (n: number) => void; total: number }; testId: string }) {
  if (p.total <= p.n) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 border-t border-slate-100 py-3 text-xs text-[#64748B]" data-testid={`${testId}-more`}>
      <span>Showing {p.n.toLocaleString()} of {p.total.toLocaleString()}. Search to find someone quickly.</span>
      <button className="btn-secondary btn-sm" onClick={() => p.setN(p.n + 200)} data-testid={`${testId}-show-more`}>Show 200 more</button>
    </div>
  );
}

export const NoMatch = ({ q, span }: { q: string; span: number }) =>
  q ? <tr><td colSpan={span} className="py-6 text-center text-sm text-[#64748B]" data-testid="dow-search-no-match">No one matches "{q}".</td></tr> : null;

export function SearchBox({ s, total, testId, placeholder }: { s: { q: string; setQ: (v: string) => void; list: any[] }; total: number; testId: string; placeholder?: string }) {
  return (
    <div className="flex w-full max-w-md items-center gap-2">
      <div className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-[#94A3B8]" />
        <input className="field-input pl-9 pr-8" placeholder={placeholder || "Search name, code, case #, email, DOB, parish\u2026"} value={s.q} onChange={(e) => s.setQ(e.target.value)} data-testid={`${testId}-search`} />
        {s.q && <button className="absolute right-2 top-2.5 rounded p-0.5 text-[#94A3B8] hover:text-[#1B365D]" onClick={() => s.setQ("")} aria-label="Clear search" data-testid={`${testId}-search-clear`}><X className="h-4 w-4" /></button>}
      </div>
      {s.q && <span className="whitespace-nowrap text-xs text-[#64748B]" data-testid={`${testId}-search-count`}>{s.list.length} of {total}</span>}
    </div>
  );
}

export function useCols(table: string, keys: string[]) {
  const hk = `dow-cols-${table}`, ok = `dow-cols-order-${table}`;
  const [hidden, setHidden] = useState<string[]>(() => read(hk));
  const [saved, setSaved] = useState<string[]>(() => read(ok));
  const all = [...saved.filter((k) => keys.includes(k)), ...keys.filter((k) => !saved.includes(k))];
  const toggle = (c: string) => {
    const next = hidden.includes(c) ? hidden.filter((x) => x !== c) : [...hidden, c];
    setHidden(next);
    localStorage.setItem(hk, JSON.stringify(next));
  };
  const reorder = (from: number, to: number) => {
    if (to < 0 || to >= all.length || from === to) return;
    const next = [...all];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setSaved(next);
    localStorage.setItem(ok, JSON.stringify(next));
  };
  const reset = () => { setHidden([]); setSaved([]); localStorage.removeItem(hk); localStorage.removeItem(ok); };
  return { all, order: all.filter((k) => !hidden.includes(k)), hidden, toggle, reorder, reset };
}

type Cols = ReturnType<typeof useCols>;

export function ColumnsMenu({ labels, cols, testId }: { labels: Record<string, string>; cols: Cols; testId: string }) {
  const [drag, setDrag] = useState<number | null>(null);
  const changed = cols.hidden.length > 0 || cols.all.join() !== Object.keys(labels).join();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="btn-secondary btn-sm" data-testid={testId} title="Show, hide or reorder columns">
          <Columns3 className="h-4 w-4" /> Columns{cols.hidden.length ? ` (${cols.hidden.length} hidden)` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid={`${testId}-panel`}>
        <p className="mb-2 text-xs text-[#64748B]">Tick to show, drag or use the arrows to reorder. Saved on this computer.</p>
        <ul className="space-y-1">
          {cols.all.map((k, i) => (
            <li key={k} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null) cols.reorder(drag, i); setDrag(null); }}
              className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm ${drag === i ? "border-[#1B365D] bg-[#F1F5F9]" : "border-slate-200 bg-white"}`} data-testid={`${testId}-item-${k}`}>
              <GripVertical className="h-4 w-4 cursor-grab text-[#94A3B8]" />
              <label className="flex flex-1 cursor-pointer items-center gap-2">
                <input type="checkbox" className="h-4 w-4 accent-[#1B365D]" checked={!cols.hidden.includes(k)} onChange={() => cols.toggle(k)} data-testid={`${testId}-${k}`} />
                {labels[k]}
              </label>
              <button className="rounded p-1 hover:bg-[#F1F5F9] disabled:opacity-30" disabled={i === 0} onClick={() => cols.reorder(i, i - 1)} title="Move left" data-testid={`${testId}-up-${k}`}><ArrowUp className="h-3.5 w-3.5" /></button>
              <button className="rounded p-1 hover:bg-[#F1F5F9] disabled:opacity-30" disabled={i === cols.all.length - 1} onClick={() => cols.reorder(i, i + 1)} title="Move right" data-testid={`${testId}-down-${k}`}><ArrowDown className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
        {changed && <button className="mt-2 text-xs font-semibold text-[#1B365D] hover:underline" onClick={cols.reset} data-testid={`${testId}-reset`}>Reset to default</button>}
      </PopoverContent>
    </Popover>
  );
}

export function ScrollBox({ children, testId }: { children: React.ReactNode; testId: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) { return; }
    const check = () => setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    check();
    el.addEventListener("scroll", check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, []);
  return (
    <>
      {more && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-1.5 text-xs font-semibold text-[#92400E]" data-testid={`${testId}-more-hint`}>
          <span>More columns to the right. Scroll sideways, or hide columns you don't need.</span>
          <button className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[#1B365D] shadow-sm hover:bg-[#F1F5F9]" onClick={() => ref.current?.scrollBy({ left: 320, behavior: "smooth" })} data-testid={`${testId}-more-scroll`}>
            Show more <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div ref={ref} className="max-h-[min(60vh,560px)] overflow-auto rounded-lg border border-slate-100 [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20 [&_thead_th]:bg-white [&_thead_th]:shadow-[0_1px_0_#E2E8F0]" data-testid={`${testId}-scroll`}>{children}</div>
    </>
  );
}
